import { useEffect, useState, type CSSProperties } from 'react';
import { useTheme } from '@vitness/theme';

export interface AvatarProps {
  /** Display name — drives the initials fallback. */
  name: string;
  /** Email — used to look up a Gravatar (tried first). */
  email?: string;
  /** Explicit image URL (e.g. Clerk's user/org image) — tried after Gravatar. */
  imageUrl?: string;
  /** Pixel size. */
  size: number;
  /** Border radius. Defaults to `'50%'` (round). Use a token for square logos. */
  radius?: string;
  style?: CSSProperties;
}

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

/**
 * Identity avatar with a source-priority fallback chain: **Gravatar** (by email
 * hash) → the explicit **imageUrl** (e.g. Clerk's photo) → an **initials** chip
 * whose background is the live theme accent. Each image source that 404s or
 * errors falls through to the next, so a missing Gravatar quietly degrades to
 * the Clerk image and finally to initials.
 */
export function Avatar({ name, email, imageUrl, size, radius = '50%', style }: AvatarProps) {
  const { accent } = useTheme();
  const [gravatar, setGravatar] = useState<string | undefined>(undefined);
  const [failedIndex, setFailedIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!email || typeof crypto === 'undefined' || !crypto.subtle) {
      setGravatar(undefined);
      return;
    }
    // `d=404` makes Gravatar 404 when the user has no avatar, so the <img>
    // onError can fall through to the next source.
    sha256Hex(email.trim().toLowerCase())
      .then((hash) => {
        if (!cancelled) setGravatar(`https://www.gravatar.com/avatar/${hash}?d=404&s=${size * 2}`);
      })
      .catch(() => {
        if (!cancelled) setGravatar(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [email, size]);

  // Priority order: Gravatar first, then the explicit image.
  const candidates = [gravatar, imageUrl].filter(Boolean) as string[];

  // Restart the chain whenever the candidate set changes (e.g. Gravatar resolves).
  useEffect(() => {
    setFailedIndex(0);
  }, [gravatar, imageUrl]);

  const src = candidates[failedIndex];

  if (src) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        onError={() => setFailedIndex((index) => index + 1)}
        style={{ width: size, height: size, borderRadius: radius, flexShrink: 0, objectFit: 'cover', ...style }}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: radius,
        background: accent,
        color: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--vn-font-display)',
        fontWeight: 700,
        fontSize: Math.round(size * 0.42),
        ...style,
      }}
    >
      {initialsOf(name)}
    </span>
  );
}
